import { createSelector } from '@reduxjs/toolkit';
import { Logger } from 'aws-amplify';
import React from 'react';

import { FlagsContext } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';
import {
  FormCompleteNotification,
  FormRequestNotification,
} from 'src/store/notifications/types';
import { isUnreadNotification } from 'src/utils/NotificationsUtils';

const logger = new Logger('FormsNotificationsBadge');

const selectFormRequestNotifications = createSelector(
  (state: RootState) => state.notifications.formRequestNotifications,
  (state: RootState) => state.activeCompany.activeCompanyId,
  (_: RootState, EnableManyCompanies: boolean) => EnableManyCompanies,
  (formRequestNotifications, activeCompanyId, EnableManyCompanies) =>
    formRequestNotifications.filter((notif) =>
      EnableManyCompanies ? notif.companyId === activeCompanyId : true,
    ) || [],
);

export const useFormsNotificationCount = (): number => {
  const { EnableManyCompanies } = React.useContext(FlagsContext);
  const isClient = useAppSelector((state: RootState) => state.user.isClient);

  const completedFormNotifs =
    useAppSelector(
      (state: RootState) => state.notifications.formCompletedNotifications,
    ) || [];
  const requestFormNotifs = useAppSelector((state) =>
    selectFormRequestNotifications(state, EnableManyCompanies),
  );

  // Get the notifications from the store according to the user role
  const notifications: FormCompleteNotification[] | FormRequestNotification[] =
    !isClient ? completedFormNotifs : requestFormNotifs;

  const [notificationsCounter, setNotificationsCounter] = React.useState(0);

  React.useEffect(() => {
    logger.debug('Setting primary forms sidebar notification');
    // filter unread notification
    logger.debug('Total notifications:', notifications.length);
    const unreadNotifications = notifications.filter(
      (notif: FormCompleteNotification | FormRequestNotification) =>
        isUnreadNotification(notif.ownerId),
    );

    logger.debug('Unread notifications:', unreadNotifications.length);

    setNotificationsCounter(unreadNotifications.length);
  }, [notifications]);

  return notificationsCounter;
};
